import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Link } from 'react-router-dom';
import { 
  Home, 
  Settings, 
  FolderOpen, 
  HelpCircle, 
  Award,
  FileText,
  Mail,
  Phone,
  TrendingUp,
  Loader2
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const cmsCards = [
    { 
      icon: Home, 
      title: 'Page d\'Accueil', 
      description: 'Textes hero, statistiques, sections',
      path: '/admin/cms/homepage',
      color: '#004534'
    },
    { 
      icon: Settings, 
      title: 'Pages Solutions', 
      description: 'Solaire, Chauffage, Isolation',
      path: '/admin/cms/solutions',
      color: '#0C6951'
    },
    { 
      icon: FolderOpen, 
      title: 'Galerie Projets', 
      description: 'Projets réalisés et photos',
      path: '/admin/cms/projects',
      color: '#D3FF62',
      badge: stats?.projects
    },
    { 
      icon: HelpCircle, 
      title: 'FAQ', 
      description: 'Questions fréquentes',
      path: '/admin/cms/faq',
      color: '#004534',
      badge: stats?.faq
    },
    { 
      icon: Award, 
      title: 'Certifications', 
      description: 'Labels et certifications',
      path: '/admin/cms/certifications',
      color: '#0C6951'
    }
  ];

  const leadCards = [
    { 
      icon: FileText, 
      title: 'Demandes de Devis', 
      count: stats?.quotes || 0,
      path: '/admin/quotes',
      color: '#004534'
    },
    { 
      icon: Mail, 
      title: 'Messages Contact', 
      count: stats?.contacts || 0,
      path: '/admin/contacts',
      color: '#D3FF62'
    },
    { 
      icon: Phone, 
      title: 'Demandes de Rappel', 
      count: stats?.callbacks || 0,
      path: '/admin/callbacks',
      color: '#0C6951'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Loader2 size={40} className="animate-spin" style={{ color: 'var(--brand-dark)' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ maxWidth: '1200px' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Tableau de Bord
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Bienvenue dans votre back-office. Gérez le contenu de votre site.
          </p>
        </div>

        {/* Section CMS */}
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Settings size={22} /> Gestion du Contenu
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
            {cmsCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <Link
                  key={index}
                  to={card.path}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    textDecoration: 'none',
                    border: '1px solid #eee',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {card.badge > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'var(--brand-primary)',
                      color: 'var(--brand-dark)',
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '2px 8px',
                      borderRadius: '10px'
                    }}>
                      {card.badge}
                    </span>
                  )}
                  <div style={{
                    width: '44px',
                    height: '44px',
                    background: card.color === '#D3FF62' ? '#D3FF62' : `${card.color}15`,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '14px'
                  }}>
                    <Icon size={22} style={{ color: card.color === '#D3FF62' ? '#004534' : card.color }} />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {card.title}
                  </h3>
                  <p style={{ fontSize: '13px', color: '#888' }}>
                    {card.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Section Leads */}
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TrendingUp size={22} /> Demandes Clients
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {leadCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <Link
                  key={index}
                  to={card.path}
                  style={{
                    background: card.color === '#D3FF62' ? '#D3FF62' : card.color,
                    borderRadius: '16px',
                    padding: '24px',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Icon size={28} style={{ color: card.color === '#D3FF62' ? '#004534' : 'white', opacity: 0.9 }} />
                  <div>
                    <div style={{ 
                      fontSize: '36px', 
                      fontWeight: '700', 
                      color: card.color === '#D3FF62' ? '#004534' : 'white',
                      lineHeight: 1
                    }}>
                      {card.count}
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: card.color === '#D3FF62' ? '#004534' : 'rgba(255,255,255,0.9)',
                      marginTop: '4px'
                    }}>
                      {card.title}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
